import { useMemo } from "react";
import { useParams } from "react-router";
import {
  useGetMediaTagsForDatasetApiV1DatasetDatasetIdTagsGet,
  useGetMediaTagsForClusterApiV1DatasetDatasetIdClusterClusterIdTagsGet,
} from "vl-api/vlComponents";
import { useGetExplorationQueryParams } from "hooks/useGetExplorationQueryParams";

export interface UserTagItem {
  id: string;
  text: string;
  selected: boolean;
}

export const useUserTagsData = (defaultFetch = true) => {
  const { datasetId, clusterId } = useParams();
  const explorationQueryParams = useGetExplorationQueryParams();

  const clusterTagsQuery =
    useGetMediaTagsForClusterApiV1DatasetDatasetIdClusterClusterIdTagsGet(
      {
        pathParams: { datasetId: datasetId!, clusterId: clusterId! },
        queryParams: explorationQueryParams,
      },
      {
        refetchOnMount: false,
        enabled: !!datasetId && !!clusterId && defaultFetch,
      },
    );

  const datasetTagsQuery =
    useGetMediaTagsForDatasetApiV1DatasetDatasetIdTagsGet(
      {
        pathParams: { datasetId: datasetId! },
        queryParams: explorationQueryParams,
      },
      {
        refetchOnMount: false,
        enabled: !!datasetId && !clusterId && defaultFetch,
      },
    );

  const data = clusterId ? clusterTagsQuery.data : datasetTagsQuery.data;
  const isLoading = clusterId
    ? clusterTagsQuery.isLoading
    : datasetTagsQuery.isLoading;

  const userTags = useMemo((): UserTagItem[] => {
    if (!data) {
      return [{ id: "untagged", text: "untagged", selected: false }];
    }

    // Sort the tags by name (case-insensitive)
    const sorted = [...data].sort((a: any, b: any) =>
      String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, {
        sensitivity: "base",
      }),
    );

    // Map to your UserTagItem shape
    const mappedTags: UserTagItem[] = sorted.map((tag: any) => ({
      id: tag.id,
      text: tag.name,
      selected: false,
    }));

    return [
      { id: "untagged", text: "untagged", selected: false },
      ...mappedTags.filter((tag) => tag.text.toLowerCase() !== "untagged"),
    ];
  }, [data]);

  return {
    isLoading,
    userTags,
  };
};
